import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Modal,
  Button,
  Table,
  Pagination,
  Form,
  Dropdown,
} from "react-bootstrap";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import { X } from "lucide-react";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const getWorkingDaysFromDates = (startMoment, endMoment) => {
  if (!moment.isMoment(startMoment) || !moment.isMoment(endMoment)) return 0;
  const s = startMoment.clone().startOf("day");
  const e = endMoment.clone().startOf("day");
  return e.diff(s, "days") + 1;
};

const AddHours = () => {
  const [employees, setEmployees] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [salarySets, setSalarySets] = useState([]);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedShift, setSelectedShift] = useState("");
  const [locations, setLocations] = useState([]);
  const [shifts, setShifts] = useState([]);

  const [normalHoursInput, setNormalHoursInput] = useState({});
  const [overtimeHoursInput, setOvertimeHoursInput] = useState({});
  const [workingDaysInput, setWorkingDaysInput] = useState({});

  const companyKey = sessionStorage.getItem("companyKey");

  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } =
    getActionPermissions(userRole);

  const safeJsonParse = (jsonString, defaultValue = null) => {
    try {
      if (!jsonString || jsonString.trim() === "") {
        return defaultValue;
      }
      return JSON.parse(jsonString);
    } catch (error) {
      console.error("JSON parsing error:", error, "Raw data:", jsonString);
      return defaultValue;
    }
  };

  const getSalaryType = (employee) => {
    const salaryData = safeJsonParse(employee.salary, []);
    if (Array.isArray(salaryData) && salaryData.length > 0) {
      const latestSalary = salaryData.sort(
        (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded)
      )[0];
      return latestSalary.salaryType || "Not Set";
    }
    return "Not Set";
  };

  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersResponse, detailsResponse] = await Promise.all([
          axios.get(
            "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
          ),
          axios.get(
            "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollEmployeeBasicDetails_PayrollEmployeeDetails"
          ),
        ]);

        const filteredUserData = usersResponse.data.filter(
          (u) => u.companyKey === companyKey
        );

        const filteredData = detailsResponse.data.filter(
          (d) => d.companyKey === companyKey
        );

        setEmployeeDetails(filteredData);

        const activeEmployees = (filteredUserData || [])
          .filter((emp) => emp.approval_status === "true")
          .reverse();

        setEmployees(activeEmployees);

        const uniqueLocations = [
          ...new Set(activeEmployees.map((emp) => emp.locationName)),
        ].filter(Boolean);
        setLocations(uniqueLocations);

        const allShifts = activeEmployees.flatMap((emp) => {
          const empShifts = safeJsonParse(emp.shift, []);
          return Array.isArray(empShifts)
            ? empShifts.map((s) => s.shiftName)
            : [];
        });
        const uniqueShifts = [...new Set(allShifts)].filter(Boolean);
        setShifts(uniqueShifts);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        const response = await axios.get(
          "https://api.dremerz.net/api/DZ_8922_EmpAttendance_EmpAttendanceDetails_AttendanceHistory"
        );
        setAttendanceData(response.data || []);
      } catch (error) {
        console.error("Error fetching attendance data:", error);
        setAttendanceData([]);
      }
    };

    fetchAttendanceData();
  }, []);

  useEffect(() => {
    const fetchSalaryHistory = async () => {
      try {
        const response = await axios.get(
          "https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList"
        );

        const filteredData = response.data.filter(
          (d) => d.companyKey === companyKey
        );
        setSalaryHistory(filteredData);
      } catch (error) {
        console.error("Error fetching salary history:", error);
        setSalaryHistory([]);
      }
    };

    fetchSalaryHistory();
  }, []);

  const hasValidPaymentTerm = (employee) => {
    const periodType = parseInt(employee.periodType) || 0;
    const payDate = employee.payDate ? moment(employee.payDate) : null;
    return periodType > 0 || payDate !== null;
  };

  const getPaymentTermText = (employee) => {
    const periodType = parseInt(employee.periodType) || 0;
    const payDate = employee.payDate ? moment(employee.payDate) : null;

    if (periodType > 0) {
      return `Payment every ${periodType} day${periodType > 1 ? "s" : ""}`;
    } else if (payDate) {
      return `Monthly payment on ${payDate.format("Do")}`;
    }
    return "No payment term specified";
  };

  // Enhanced processing with user input hours or working days
  const handleProcessSalary = async (employee) => {
    console.log("Processing salary for employee:", employee.employee_id);

    if (!hasValidPaymentTerm(employee)) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "No payment term specified",
        message:
          "Cannot process salary: No payment term specified for this employee. Please set up period type or pay date first.",
        duration: 2500, // auto-close in 2.5s
      });

      return;
    }

    const salaryType = getSalaryType(employee);

    if (salaryType === "hourly") {
      const normalHours = normalHoursInput[employee.id] || "0h 0m";
      const overtimeHours = overtimeHoursInput[employee.id] || "0h 0m";

      if (normalHours === "0h 0m" && overtimeHours === "0h 0m") {
        showMyAlert({
          type: "info", // "success" | "danger" | "warning" | "info"
          title: "Info",
          message: "Please enter working hours before processing salary.",
          duration: 2500, // auto-close in 2.5s
        });

        return;
      }

      const timeRegex = /^(\d+h\s?\d*m?)?$/;

      const parseHours = (value) => {
        if (!value) return 0;

        if (!timeRegex.test(value)) {
          showMyAlert({
            type: "info", // "success" | "danger" | "warning" | "info"
            title: "Info",
            message: "Invalid time format! Please use format like '8h 30m'.",
            duration: 2500, // auto-close in 2.5s
          });
          throw new Error("Invalid format");
        }

        const match = value.match(/(\d+)h\s*(\d+)?m?/);
        if (!match) return 0;

        const hours = parseInt(match[1] || "0", 10);
        const minutes = parseInt(match[2] || "0", 10);
        return hours * 60 + minutes; // total minutes
      };

      try {
        const totalNormalMinutes = parseHours(normalHours);
        const totalOvertimeMinutes = parseHours(overtimeHours);

        if (totalNormalMinutes === 0 && totalOvertimeMinutes === 0) {
          showMyAlert({
            type: "info", // "success" | "danger" | "warning" | "info"
            title: "Info",
            message: "Please enter working hours before processing salary.",
            duration: 2500, // auto-close in 2.5s
          });

          return;
        }
      } catch (err) {
        return;
      }
    } else {
      const workingDays = workingDaysInput[employee.id] || "";

      if (!workingDays || workingDays.trim() === "") {
        showMyAlert({
          type: "info", // "success" | "danger" | "warning" | "info"
          title: "Info",
          message: "Please enter working days before processing salary.",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      }

      const days = parseInt(workingDays);
      if (isNaN(days) || days < 0) {
        showMyAlert({
          type: "info", // "success" | "danger" | "warning" | "info"
          title: "Info",
          message: "Please enter valid number of working days",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      }
    }

    setSelectedEmployee(employee);

    try {
      const currentSet = calculateCurrentSalarySetWithInput(
        employee,
        salaryType
      );

      if (!currentSet) {
        showMyAlert({
          type: "info", // "success" | "danger" | "warning" | "info"
          title: "Info",
          message:
            "Unable to calculate salary. Please check employee salary configuration and payment terms.",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      }

      const salaryRecordsResponse = await axios.get(
        "https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList"
      );

      const allRecords = Array.isArray(salaryRecordsResponse.data)
        ? salaryRecordsResponse.data
        : [];

      const filteredRecords = allRecords.filter(
        (a) => a.companyKey === companyKey
      );
      const employeeRecords = filteredRecords.filter(
        (record) => record.employee_id === employee.employee_id
      );

      const historicalRecords = employeeRecords.map((record) => ({
        ...record,
        startDate: record.startDate || moment().format("YYYY-MM-DD"),
        endDate: record.endDate || moment().format("YYYY-MM-DD"),
        isPaid: true,
        isExisting: true,
        totalWorkingHours: record.normaltotalWorkingHours || "0h 0m",
        overTimeHours: record.ottotalWorkingHours || "0h 0m",
        calculatedSalary: record.normalcalculatedSalary || "0.00",
        overtimeSalary: record.otcalculatedSalary || "0.00",
        totalSalary: record.overallcalculatedSalary || "0.00",
        workingDays: parseInt(record.workingDays || 0),
        hasPaymentTerm: true,
        status: "completed",
      }));

      const alreadyPaid = historicalRecords.some(
        (record) =>
          record.startDate === currentSet.startDate &&
          record.endDate === currentSet.endDate
      );

      if (alreadyPaid) {
        currentSet.isPaid = true;
        currentSet.canPayToday = false;
      }

      const combinedSets = [currentSet, ...historicalRecords];
      setSalarySets(combinedSets);
      setShowModal(true);
    } catch (error) {
      console.error("Error processing salary:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Failed",
        message: "Error processing salary. Please try again.",
        duration: 2500, // auto-close in 2.5s
      });
    }
  };

  const calculateCurrentSalarySetWithInput = (employee, salaryType) => {
    const today = moment();
    const periodType = parseInt(employee.periodType) || 0;
    const payDate = employee.payDate ? moment(employee.payDate) : null;
    const approvalDate = moment(employee.approval_date, "DD-MM-YYYY");

    const salaryData = safeJsonParse(employee.salary, []);
    const overTimeSalaryData = safeJsonParse(employee.overTimeSalary, []);

    const latestSalary =
      Array.isArray(salaryData) && salaryData.length > 0
        ? salaryData.sort(
            (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded)
          )[0]
        : null;

    const latestOverTimeSalary =
      Array.isArray(overTimeSalaryData) && overTimeSalaryData.length > 0
        ? overTimeSalaryData.sort(
            (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded)
          )[0]
        : null;

    if (!latestSalary || !latestSalary.amount) {
      console.error(
        "No valid salary data found for employee:",
        employee.employee_id
      );
      return null;
    }

    let currentStart, currentEnd;

    if (periodType > 0) {
      if (periodType === 1) {
        currentStart = today.clone().startOf("day");
        currentEnd = today.clone().endOf("day");
      } else {
        const daysSinceApproval = today.diff(approvalDate, "days");
        const currentPeriodNumber = Math.floor(daysSinceApproval / periodType);
        currentStart = approvalDate
          .clone()
          .add(currentPeriodNumber * periodType, "days");
        currentEnd = currentStart
          .clone()
          .add(periodType - 1, "days")
          .endOf("day");
      }
    } else if (payDate) {
      currentEnd = payDate.clone();
      if (currentEnd.date() > today.date()) {
        currentEnd.subtract(1, "month");
      }
      currentStart = currentEnd.clone().subtract(1, "month").add(1, "day");
    }

    const totalCalendarDaysInPeriod = getWorkingDaysFromDates(
      currentStart,
      currentEnd
    );

    let userWorkingDays, userNormalHours, userOvertimeHours;

    if (salaryType === "fixed") {
      userWorkingDays = parseInt(workingDaysInput[employee.id] || "0");
      userNormalHours = "0h 0m";
      userOvertimeHours = overtimeHoursInput[employee.id] || "0h 0m";
    } else {
      userWorkingDays = totalCalendarDaysInPeriod;
      userNormalHours = normalHoursInput[employee.id] || "0h 0m";
      userOvertimeHours = overtimeHoursInput[employee.id] || "0h 0m";
    }

    const isComplete =
      currentEnd.isBefore(today) || currentEnd.isSame(today, "day");

    const setData = {
      startDate: currentStart.format("YYYY-MM-DD"),
      endDate: currentEnd.format("YYYY-MM-DD"),
      status: isComplete ? "completed" : "onProcess",
      periodType: periodType > 0 ? periodType : null,
      payDate: payDate ? payDate.date() : null,
      totalWorkingHours: userNormalHours,
      overTimeHours: userOvertimeHours,
      workingDays: userWorkingDays,
      totalDaysInPeriod: totalCalendarDaysInPeriod,
      workType: salaryType === "fixed" ? "fixed-salary" : "hourly",
      isPaid: false,
      isExisting: false,
      hasPaymentTerm: true,
      canPayToday: isComplete,
      salaryType: salaryType,
      periodStartDisplay: currentStart.format("DD-MM-YYYY"),
      periodEndDisplay: currentEnd.format("DD-MM-YYYY"),
      totalPeriodDays: totalCalendarDaysInPeriod,
    };

    const salaryAmount = parseFloat(latestSalary.amount);
    if (isNaN(salaryAmount)) {
      console.error("Invalid salary amount:", latestSalary.amount);
      return null;
    }

    if (salaryType === "hourly") {
      // Hourly salary calculation
      const hours = parseHoursToDecimal(setData.totalWorkingHours);
      const regularSalary = (hours * salaryAmount).toFixed(2);

      let overtimeSalary = "0.00";
      if (setData.overTimeHours !== "0h 0m" && latestOverTimeSalary?.amount) {
        const overtimeAmount = parseFloat(latestOverTimeSalary.amount);
        if (!isNaN(overtimeAmount)) {
          const overtimeHrs = parseHoursToDecimal(setData.overTimeHours);
          overtimeSalary = (overtimeHrs * overtimeAmount).toFixed(2);
        }
      }

      setData.calculatedSalary = regularSalary;
      setData.overtimeSalary = overtimeSalary;
      setData.totalSalary = (
        parseFloat(regularSalary) + parseFloat(overtimeSalary)
      ).toFixed(2);
    } else {
      const dailyRate = salaryAmount / totalCalendarDaysInPeriod;
      const regularSalary = Math.round(dailyRate * userWorkingDays * 100) / 100;

      let overtimeSalary = 0;
      if (setData.overTimeHours !== "0h 0m" && latestOverTimeSalary?.amount) {
        const overtimeAmount = parseFloat(latestOverTimeSalary.amount);
        if (!isNaN(overtimeAmount)) {
          const overtimeHrs = parseHoursToDecimal(setData.overTimeHours);
          overtimeSalary = Math.round(overtimeHrs * overtimeAmount * 100) / 100;
        }
      }
      setData.calculatedSalary = regularSalary.toFixed(2);
      setData.overtimeSalary = overtimeSalary.toFixed(2);
      setData.totalSalary = (regularSalary + overtimeSalary).toFixed(2);
      setData.dailyRate = dailyRate.toFixed(2);
    }

    return setData;
  };

  const parseHoursToDecimal = (timeStr) => {
    if (!timeStr || timeStr === "0h 0m") return 0;
    const hourMatch = timeStr.match(/(\d+)h/);
    const minuteMatch = timeStr.match(/(\d+)m/);
    const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
    const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0;
    return hours + minutes / 60;
  };

  const addTimeStrings = (timeStr1, timeStr2) => {
    const parse = (str) => {
      if (!str) return { hours: 0, minutes: 0 };
      const hourMatch = str.match(/(\d+)h/);
      const minuteMatch = str.match(/(\d+)m/);
      const hours = hourMatch ? parseInt(hourMatch[1]) : 0;
      const minutes = minuteMatch ? parseInt(minuteMatch[1]) : 0;
      return { hours, minutes };
    };

    const t1 = parse(timeStr1);
    const t2 = parse(timeStr2);

    let totalMinutes = t1.minutes + t2.minutes;
    let totalHours = t1.hours + t2.hours + Math.floor(totalMinutes / 60);
    totalMinutes = totalMinutes % 60;

    return `${totalHours}h ${totalMinutes}m`;
  };

  // Pay salary function
  const handlePaySalary = async (setData) => {
    try {
      const salaryData = safeJsonParse(selectedEmployee.salary, []);
      const overTimeSalaryData = safeJsonParse(
        selectedEmployee.overTimeSalary,
        []
      );

      if (!Array.isArray(salaryData) || salaryData.length === 0) {
        showMyAlert({
          type: "info", // "success" | "danger" | "warning" | "info"
          title: "No data",
          message: "Employee salary data is not available",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      }

      const latestSalary = salaryData.sort(
        (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded)
      )[0];
      const latestOverTimeSalary =
        Array.isArray(overTimeSalaryData) && overTimeSalaryData.length > 0
          ? overTimeSalaryData.sort(
              (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded)
            )[0]
          : null;

      const payload = {
        userId: selectedEmployee.id,
        userName: selectedEmployee.full_name,
        employee_id: selectedEmployee.employee_id,
        email: selectedEmployee.email || "",
        mobileNumber: selectedEmployee.mobile_number,
        workingDays: setData.workingDays.toString(),
        salaryType: latestSalary.salaryType,
        overTimeRate: latestOverTimeSalary ? latestOverTimeSalary.amount : "0",
        regularRate: latestSalary.amount,
        createdAt: moment().format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
        startDate: setData.startDate,
        endDate: setData.endDate,
        normaltotalWorkingHours: setData.totalWorkingHours,
        ottotalWorkingHours: setData.overTimeHours || "0h 0m",
        overalltotalWorkingHours: addTimeStrings(
          setData.totalWorkingHours,
          setData.overTimeHours
        ),
        normalcalculatedSalary: setData.calculatedSalary,
        otcalculatedSalary: setData.overtimeSalary || "0.00",
        overallcalculatedSalary: setData.totalSalary,
        companyKey,
      };

      await axios.post(
        "https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList",
        payload
      );

      doAuditLog("Add", "Add Hours & Pay salary", "", payload);

      // Clear input fields
      setNormalHoursInput((prev) => ({ ...prev, [selectedEmployee.id]: "" }));
      setOvertimeHoursInput((prev) => ({ ...prev, [selectedEmployee.id]: "" }));
      setWorkingDaysInput((prev) => ({ ...prev, [selectedEmployee.id]: "" }));

      // Refresh salary history
      const response = await axios.get(
        "https://api.dremerz.net/api/DZ_8924_Payroll_PayrollSalaryCalculation_PayrollAddSalary_PayrollSalaryList"
      );
      const filteredData = response.data.filter(
        (d) => d.companyKey === companyKey
      );
      setSalaryHistory(filteredData);

      // Update the current set to mark as paid
      const updatedSets = salarySets.map((set) => {
        if (
          set.startDate === setData.startDate &&
          set.endDate === setData.endDate &&
          !set.isExisting
        ) {
          return { ...set, isPaid: true, canPayToday: false };
        }
        return set;
      });
      setSalarySets(updatedSets);

      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Success",
        message: "Salary Paid successfully",
        duration: 2500, // auto-close in 2.5s
      });
      setShowModal(false);
    } catch (error) {
      console.error("Error paying salary:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Failed",
        message: "Error processing salary. Please try again.",
        duration: 2500, // auto-close in 2.5s
      });
    }
  };

  const renderShiftInfo = (shiftJson) => {
    const shifts = safeJsonParse(shiftJson, []);
    if (Array.isArray(shifts) && shifts.length > 0) {
      return shifts[0].shiftName || "N/A";
    }
    return "N/A";
  };

  const getSalaryDisplay = (employee) => {
    const salaryData = safeJsonParse(employee.salary, []);
    const overTimeSalaryData = safeJsonParse(employee.overTimeSalary, []);

    if (!Array.isArray(salaryData) || salaryData.length === 0) {
      return "No salary data";
    }

    const latestSalary = salaryData.sort(
      (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded)
    )[0];

    const latestOverTimeSalary =
      Array.isArray(overTimeSalaryData) && overTimeSalaryData.length > 0
        ? overTimeSalaryData.sort(
            (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded)
          )[0]
        : null;

    if (!latestSalary) {
      return "No salary data";
    }

    let displayText =
      latestSalary.salaryType === "hourly"
        ? `${latestSalary.amount}/hour`
        : `Fixed: ${latestSalary.amount}`;

    if (latestOverTimeSalary) {
      displayText += ` | OT: ${latestOverTimeSalary.amount}/hour`;
    }

    return displayText;
  };

  // Filter employees based on search term and selected filters
  const filteredEmployees = Array.isArray(employees)
    ? employees.filter((employee) => {
        const matchesSearch =
          employee.full_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          employee.employee_id
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          employee.mobile_number?.includes(searchTerm) ||
          employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          employee.locationName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          renderShiftInfo(employee.shift)
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());

        const matchesLocation =
          !selectedLocation || employee.locationName === selectedLocation;
        const matchesShift =
          !selectedShift || renderShiftInfo(employee.shift) === selectedShift;

        return matchesSearch && matchesLocation && matchesShift;
      })
    : [];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredEmployees.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
    setTotalPages(totalPages);

    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredEmployees, itemsPerPage, currentPage]);

  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  };

  // Get salary processing status for an employee
  const getSalaryProcessingStatus = (employee) => {
    const today = moment();
    const periodType = parseInt(employee.periodType) || 0;
    const payDate = employee.payDate ? moment(employee.payDate) : null;
    const approvalDate = moment(employee.approval_date, "DD-MM-YYYY");

    if (!hasValidPaymentTerm(employee)) {
      return {
        canProcess: false,
        message: "No Payment Term",
        variant: "outline-secondary",
        title: "No payment term specified for this employee",
      };
    }

    const salaryType = getSalaryType(employee);

    // Check if there are input data based on salary type
    let hasInput = false;
    if (salaryType === "hourly") {
      hasInput =
        normalHoursInput[employee.id] || overtimeHoursInput[employee.id];
    } else {
      hasInput = workingDaysInput[employee.id];
    }

    if (!hasInput) {
      const inputType =
        salaryType === "hourly" ? "working hours" : "working days";
      return {
        canProcess: false,
        message: `Enter ${inputType} First`,
        variant: "outline-primary",
        title: `Please enter ${inputType} before processing salary`,
      };
    }

    // Check if salary was already processed for current period
    let currentPeriodStart, currentPeriodEnd;

    if (periodType > 0) {
      if (periodType === 1) {
        // Daily payment
        currentPeriodStart = today.clone().startOf("day");
        currentPeriodEnd = today.clone().endOf("day");
      } else {
        // Multi-day periods
        const daysSinceApproval = today.diff(approvalDate, "days");
        const currentPeriodNumber = Math.floor(daysSinceApproval / periodType);
        currentPeriodStart = approvalDate
          .clone()
          .add(currentPeriodNumber * periodType, "days");
        currentPeriodEnd = currentPeriodStart
          .clone()
          .add(periodType - 1, "days")
          .endOf("day");
      }
    } else if (payDate) {
      currentPeriodEnd = payDate.clone();
      if (currentPeriodEnd.date() > today.date()) {
        currentPeriodEnd.subtract(1, "month");
      }
      currentPeriodStart = currentPeriodEnd
        .clone()
        .subtract(1, "month")
        .add(1, "day");
    }

    // Check if already paid for this period
    const alreadyPaid = salaryHistory.some(
      (record) =>
        record.employee_id === employee.employee_id &&
        record.startDate === currentPeriodStart.format("YYYY-MM-DD") &&
        record.endDate === currentPeriodEnd.format("YYYY-MM-DD")
    );

    if (alreadyPaid) {
      return {
        canProcess: false,
        message: "Already Processed",
        variant: "success",
        title: `Salary already processed for period ${currentPeriodStart.format(
          "DD-MM-YYYY"
        )} to ${currentPeriodEnd.format("DD-MM-YYYY")}`,
      };
    }

    // Check if period is complete
    const isPeriodComplete =
      currentPeriodEnd.isBefore(today) || currentPeriodEnd.isSame(today, "day");

    if (periodType === 1) {
      // Daily payment - can process today
      return {
        canProcess: true,
        message: "Process Today's Salary",
        variant: "primary",
        title: "Process salary for today",
      };
    } else if (isPeriodComplete) {
      return {
        canProcess: true,
        message: "Process Salary",
        variant: "primary",
        title: `Process salary for period ${currentPeriodStart.format(
          "DD-MM-YYYY"
        )} to ${currentPeriodEnd.format("DD-MM-YYYY")}`,
      };
    } else {
      return {
        canProcess: false,
        message: "Period Incomplete",
        variant: "warning",
        title: `Period ends on ${currentPeriodEnd.format("DD-MM-YYYY")}`,
      };
    }
  };

  const isPaymentTermMissing = (employee) => {
    const term = getPaymentTermText(employee)?.trim().toLowerCase();
    return term === "no payment term specified";
  };

  const isMobile = window.innerWidth < 768;

  // Render input field based on salary type
  const renderInputField = (employee) => {
    const salaryType = getSalaryType(employee);
    const isDisabled = isPaymentTermMissing(employee);

    if (salaryType === "hourly") {
      return (
        <>
          <tr>
            <td className="fw-bold">Normal Hours</td>
            <td>
              <Form.Control
                type="text"
                placeholder="e.g., 8h 0m"
                value={normalHoursInput[employee.id] || ""}
                onChange={(e) => {
                  setNormalHoursInput({
                    ...normalHoursInput,
                    [employee.id]: e.target.value,
                  });
                }}
                size="sm"
                disabled={isDisabled}
                title={
                  isDisabled
                    ? "Payment term not specified — cannot enter hours"
                    : ""
                }
              />
            </td>
          </tr>
          <tr>
            <td className="fw-bold">Overtime Hours</td>
            <td>
              <Form.Control
                type="text"
                placeholder="e.g., 2h 30m"
                value={overtimeHoursInput[employee.id] || ""}
                onChange={(e) =>
                  setOvertimeHoursInput({
                    ...overtimeHoursInput,
                    [employee.id]: e.target.value,
                  })
                }
                size="sm"
                disabled={isDisabled}
                title={
                  isDisabled
                    ? "Payment term not specified — cannot enter hours"
                    : ""
                }
              />
            </td>
          </tr>
        </>
      );
    } else {
      return (
        <>
          <tr>
            <td className="fw-bold">Working Days</td>
            <td>
              <Form.Control
                type="number"
                placeholder="e.g., 28"
                value={workingDaysInput[employee.id] || ""}
                onChange={(e) => {
                  setWorkingDaysInput({
                    ...workingDaysInput,
                    [employee.id]: e.target.value,
                  });
                }}
                size="sm"
                disabled={isDisabled}
                min="0"
                max="31"
                title={
                  isDisabled
                    ? "Payment term not specified — cannot enter days"
                    : ""
                }
              />
            </td>
          </tr>
          <tr>
            <td className="fw-bold">Overtime Hours</td>
            <td>
              <Form.Control
                type="text"
                placeholder="e.g., 2h 30m"
                value={overtimeHoursInput[employee.id] || ""}
                onChange={(e) =>
                  setOvertimeHoursInput({
                    ...overtimeHoursInput,
                    [employee.id]: e.target.value,
                  })
                }
                size="sm"
                disabled={isDisabled}
                title={
                  isDisabled
                    ? "Payment term not specified — cannot enter hours"
                    : ""
                }
              />
            </td>
          </tr>
        </>
      );
    }
  };

  // Render desktop input fields based on salary type
  const renderDesktopInputFields = (employee) => {
    const salaryType = getSalaryType(employee);
    const isDisabled = isPaymentTermMissing(employee);

    if (salaryType === "hourly") {
      return (
        <>
          <td>
            <Form.Control
              type="text"
              placeholder="e.g., 8h 0m"
              value={normalHoursInput[employee.id] || ""}
              onChange={(e) =>
                setNormalHoursInput({
                  ...normalHoursInput,
                  [employee.id]: e.target.value,
                })
              }
              size="sm"
              disabled={isDisabled}
              title={
                isDisabled
                  ? "Payment term not specified — cannot enter hours"
                  : ""
              }
            />
          </td>
          <td>
            <Form.Control
              type="text"
              placeholder="e.g., 2h 30m"
              value={overtimeHoursInput[employee.id] || ""}
              onChange={(e) =>
                setOvertimeHoursInput({
                  ...overtimeHoursInput,
                  [employee.id]: e.target.value,
                })
              }
              size="sm"
              disabled={isDisabled}
              title={
                isDisabled
                  ? "Payment term not specified — cannot enter hours"
                  : ""
              }
            />
          </td>
        </>
      );
    } else {
      return (
        <>
          <td>
            <Form.Control
              type="number"
              placeholder="e.g., 28"
              value={workingDaysInput[employee.id] || ""}
              onChange={(e) =>
                setWorkingDaysInput({
                  ...workingDaysInput,
                  [employee.id]: e.target.value,
                })
              }
              size="sm"
              disabled={isDisabled}
              min="0"
              max="31"
              title={
                isDisabled
                  ? "Payment term not specified — cannot enter days"
                  : ""
              }
            />
          </td>
          <td>
            <Form.Control
              type="text"
              placeholder="e.g., 2h 30m"
              value={overtimeHoursInput[employee.id] || ""}
              onChange={(e) =>
                setOvertimeHoursInput({
                  ...overtimeHoursInput,
                  [employee.id]: e.target.value,
                })
              }
              size="sm"
              disabled={isDisabled}
              title={
                isDisabled
                  ? "Payment term not specified — cannot enter hours"
                  : ""
              }
            />
          </td>
        </>
      );
    }
  };

  return (
    <div className="container-fluid mt-3">
      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-1">
        <div className="col-12">
          <h5 className="mb-0 d-flex align-items-center">
            Employee Salary Processing
          </h5>
        </div>
      </div>

      <div className="row align-items-center mb-3 border rounded bg-light py-3 mx-1">
        <div className="col-12 col-md-4 mb-2 mb-md-0">
          <label className="form-label small text-muted mb-1">Search</label>
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-light border-end-0">
              <FaSearch className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="col-12 col-md-3 mb-2 mb-md-0">
          <label className="form-label small text-muted mb-1">Location</label>
          <Form.Select
            size="sm"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <option value="">All Locations</option>
            {locations.map((location, index) => (
              <option key={index} value={location}>
                {location}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="col-12 col-md-3 mb-2 mb-md-0">
          <label className="form-label small text-muted mb-1">Shift</label>
          <Form.Select
            size="sm"
            value={selectedShift}
            onChange={(e) => setSelectedShift(e.target.value)}
          >
            <option value="">All Shifts</option>
            {shifts.map((shift, index) => (
              <option key={index} value={shift}>
                {shift}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="col-12 col-md-2 d-flex align-items-end mt-2  mt-md-4">
          <Button
            variant="outline-danger"
            size="sm"
            className="form-control"
            onClick={() => {
              setSearchTerm("");
              setSelectedLocation("");
              setSelectedShift("");
            }}
            disabled={!searchTerm && !selectedLocation && !selectedShift}
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Pagination Section */}
      {filteredEmployees.length > 0 && (
        <div className="row align-items-center justify-content-between mb-2 p-2">
          {/* Items per page selector */}
          <div className="col-5 col-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="items-per-page-dropdown"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Pagination controls */}
          <div className="col-7 col-md-auto">
            <Pagination
              size="sm"
              className="mb-0 justify-content-center justify-content-md-end"
            >
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {/* Mobile: Show simplified pagination */}
              {isMobile ? (
                <>
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && (
                    <>
                      <Pagination.Ellipsis disabled />
                      <Pagination.Item
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              ) : (
                /* Desktop: Show smart pagination */
                Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let page;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }
                  return (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })
              )}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}
      {isMobile ? (
        <div>
          {currentRecords.length > 0 ? (
            currentRecords.map((employee, index) => {
              const status = getSalaryProcessingStatus(employee);
              const salaryType = getSalaryType(employee);

              return (
                <div key={employee.id} className="mb-3">
                  <div
                    className="card shadow-sm"
                    style={{ textAlign: "left", borderRadius: "12px" }}
                  >
                    <div className="card-body table-responsive">
                      <table className="table table-striped table-bordered mb-2">
                        <tbody>
                          <tr>
                            <td className="fw-bold">SI</td>
                            <td>{filteredEmployees.length - index}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Name</td>
                            <td>{employee.full_name || "N/A"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Employee ID</td>
                            <td>{employee.employee_id || "N/A"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Mobile</td>
                            <td>{employee.mobile_number || "N/A"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Location</td>
                            <td>{employee.locationName || "N/A"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Shift</td>
                            <td>{renderShiftInfo(employee.shift)}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Salary Type</td>
                            <td>
                              {salaryType === "hourly" ? "HOURLY" : "FIXED"}
                            </td>
                          </tr>
                          <tr>
                            <td className="fw-bold">Payment Term</td>
                            <td>
                              <small>{getPaymentTermText(employee)}</small>
                            </td>
                          </tr>
                          {renderInputField(employee)}
                        </tbody>
                      </table>
                      <div className="d-flex justify-content-end align-itmes-center gap-2 flex-wrap">
                        {canAdd && (
                          <Button
                            variant={status.variant}
                            size="sm"
                            onClick={() => handleProcessSalary(employee)}
                            disabled={!status.canProcess}
                          >
                            {status.message}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-4">
              No employees found matching your criteria
            </div>
          )}
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th id="text-white-important">SI</th>
              <th id="text-white-important">Employee Name</th>
              <th id="text-white-important">Employee ID</th>
              <th id="text-white-important">Mobile Number</th>
              <th id="text-white-important">Location</th>
              <th id="text-white-important">Shift</th>
              <th id="text-white-important">Salary Type</th>
              <th id="text-white-important">Payment Term</th>
              {getSalaryType(currentRecords[0] || {}) === "hourly" ? (
                <>
                  <th id="text-white-important">Normal Hours</th>
                  <th id="text-white-important">Overtime Hours</th>
                </>
              ) : (
                <>
                  <th id="text-white-important">Working Days/Normal Hours</th>
                  <th id="text-white-important">Overtime Hours</th>
                </>
              )}
              <th id="text-white-important">Action</th>
            </tr>
          </thead>
          <tbody>
            {currentRecords.length > 0 ? (
              currentRecords.map((employee, index) => {
                const status = getSalaryProcessingStatus(employee);
                const salaryType = getSalaryType(employee);

                return (
                  <tr key={employee.id}>
                    <td>{filteredEmployees.length - index}</td>
                    <td>{employee.full_name || "N/A"}</td>
                    <td>{employee.employee_id || "N/A"}</td>
                    <td>{employee.mobile_number || "N/A"}</td>
                    <td>{employee.locationName || "N/A"}</td>
                    <td>{renderShiftInfo(employee.shift)}</td>
                    <td>
                      {salaryType === "hourly"
                        ? "HOURLY"
                        : salaryType === "fixed"
                        ? "FIXED"
                        : "Not Set"}
                    </td>
                    <td>
                      <small>{getPaymentTermText(employee)}</small>
                    </td>
                    {renderDesktopInputFields(employee)}
                    <td style={{ width: "150px" }}>
                      <Button
                        variant={status.variant}
                        size="sm"
                        onClick={() =>
                          userRole !== "superadmin"
                            ? showMyAlert({
                                type: "danger", // "success" | "danger" | "warning" | "info"
                                title: "No Permission",
                                message: "Only SuperAdmin Can Make payment",
                                duration: 2500, // auto-close in 2.5s
                              })
                            : handleProcessSalary(employee)
                        }
                        disabled={!status.canProcess}
                        title={
                          userRole !== "superadmin"
                            ? "Only superadmin can process payments"
                            : status.title
                        }
                      >
                        {status.message}
                      </Button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="11" className="text-center">
                  No employees found matching your criteria
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      {/* Showing entries info */}
      <div className="col-12 mb-2 text-end">
        <span className="text-muted">
          Showing {indexOfFirstItem + 1} to{" "}
          {Math.min(indexOfLastItem, filteredEmployees.length)} of{" "}
          {filteredEmployees.length} entries
        </span>
      </div>

      {/* Salary Calculation Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        style={{ zIndex: "99999" }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Salary Calculation for {selectedEmployee?.full_name} (
            {selectedEmployee?.employee_id})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmployee && (
            <div>
              <div className="d-flex justify-content-between mb-3">
                <div>
                  <h5>Current Salary: </h5>
                  <p>{getSalaryDisplay(selectedEmployee)}</p>
                </div>
                <div>
                  <h5>Payment Term: </h5>
                  <p>{getPaymentTermText(selectedEmployee)}</p>
                </div>
                <div>
                  <h5>Salary Type: </h5>
                  <p>
                    {getSalaryType(selectedEmployee) === "hourly"
                      ? "HOURLY"
                      : "FIXED"}
                  </p>
                </div>
              </div>

              <h5 className="mt-4">Salary Sets:</h5>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th id="text-white-important">Period</th>
                    {getSalaryType(selectedEmployee) === "fixed" && (
                      <th id="text-white-important">Total Days</th>
                    )}
                    <th id="text-white-important">Working Days</th>

                    <th id="text-white-important">Work Hours</th>
                    <th id="text-white-important">Overtime Hours</th>
                    <th id="text-white-important">Overall Hours</th>
                    <th id="text-white-important">Regular Salary</th>
                    <th id="text-white-important">Overtime Salary</th>
                    <th id="text-white-important">Total Salary</th>
                    {getSalaryType(selectedEmployee) === "fixed" && (
                      <th id="text-white-important">Daily Rate</th>
                    )}
                    <th id="text-white-important">Status</th>
                    <th id="text-white-important">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {salarySets
                    .slice(0, showAllHistory ? salarySets.length : 5)
                    .map((set, index) => {
                      const isValidForPayment = set.status === "completed";
                      const isPaid = set.isPaid;
                      const isCurrentSet = !set.isExisting;
                      const isZeroSalary =
                        parseFloat(set.totalSalary || 0) <= 0;
                      const canPayToday = set.canPayToday && !isPaid;

                      let buttonDisabled = true;
                      let buttonVariant = "secondary";
                      let buttonText = "Not Available";
                      let buttonTitle = "";

                      if (isPaid) {
                        buttonText = "Paid";
                        buttonVariant = "success";
                        buttonTitle = "This salary has already been paid";
                      } else if (isZeroSalary) {
                        buttonText = "No Earnings";
                        buttonTitle = "No salary to pay for this period";
                      } else if (isCurrentSet && canPayToday) {
                        buttonDisabled = false;
                        buttonVariant = "primary";
                        buttonText = "Pay Salary";
                        buttonTitle = "Click to pay salary for this period";
                      } else if (isCurrentSet) {
                        buttonText = "Period Incomplete";
                        buttonTitle = "Period is not yet complete";
                      } else {
                        buttonText = "Historical";
                        buttonTitle = "This is a historical record";
                      }

                      return (
                        <tr key={index}>
                          <td
                            style={{ minWidth: "120px", whiteSpace: "nowrap" }}
                          >
                            {set.periodStartDisplay ||
                              moment(set.startDate).format("DD-MM-YYYY")}{" "}
                            to{" "}
                            {set.periodEndDisplay ||
                              moment(set.endDate).format("DD-MM-YYYY")}
                          </td>
                          {getSalaryType(selectedEmployee) === "fixed" && (
                            <td>
                              {set.totalPeriodDays ||
                                set.totalDaysInPeriod ||
                                "-"}
                            </td>
                          )}
                          <td>{set.workingDays || "-"}</td>

                          <td>{set.totalWorkingHours || "-"}</td>
                          <td>{set.overTimeHours || "0h 0m"}</td>
                          <td>
                            {addTimeStrings(
                              set.totalWorkingHours,
                              set.overTimeHours
                            )}
                          </td>
                          <td>
                            {set.calculatedSalary
                              ? `₹${set.calculatedSalary}`
                              : "-"}
                          </td>
                          <td>
                            {set.overtimeSalary
                              ? `₹${set.overtimeSalary}`
                              : "₹0.00"}
                          </td>
                          <td>
                            {set.totalSalary ? `₹${set.totalSalary}` : "-"}
                          </td>
                          {getSalaryType(selectedEmployee) === "fixed" && (
                            <td>{set.dailyRate ? `₹${set.dailyRate}` : "-"}</td>
                          )}
                          <td>
                            {isPaid ? (
                              <span className="text-success">Paid</span>
                            ) : isZeroSalary ? (
                              <span className="text-danger">No Earnings</span>
                            ) : isCurrentSet && canPayToday ? (
                              <span className="text-primary">
                                Ready for Payment
                              </span>
                            ) : isCurrentSet ? (
                              <span className="text-warning">
                                Period Incomplete
                              </span>
                            ) : (
                              <span className="text-info">
                                Historical Record
                              </span>
                            )}
                          </td>
                          <td>
                            {canAdd && (
                              <Button
                                variant={buttonVariant}
                                size="sm"
                                onClick={() =>
                                  userRole !== "superadmin"
                                    ? showMyAlert({
                                        type: "danger", // "success" | "danger" | "warning" | "info"
                                        title: "No Permission",
                                        message:
                                          "Only SuperAdmin Can Make payment",
                                        duration: 2500, // auto-close in 2.5s
                                      })
                                    : !buttonDisabled && handlePaySalary(set)
                                }
                                title={
                                  userRole !== "superadmin"
                                    ? "Only superadmin can process payments"
                                    : buttonTitle
                                }
                              >
                                {buttonText}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>

              {salarySets.length > 5 && (
                <Button
                  variant="link"
                  onClick={() => setShowAllHistory(!showAllHistory)}
                >
                  {showAllHistory ? "Show Less" : "Show All History"}
                </Button>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AddHours;
